/**
 * A model's answer as text rather than as its markup.
 *
 * Models trained on documents answer in Markdown by habit: `**bold**`, `### headings`,
 * `- bullets`. The portal renders an answer as text, so those characters reached the customer
 * literally — the walk read `**Lid microswitch**` and `### Fault codes` on screen (W8-UI-07).
 *
 * The markers are removed and what they marked is kept; nothing is styled, so no answer gains
 * emphasis the model did not earn and no new element appears on the page. A fenced block keeps
 * its lines and loses only its fence, and a link keeps its words with its address beside them.
 */
const RULES: [RegExp, string][] = [
  // Fences and the language tag on them.
  [/^\s*```[^\n]*$/gm, ""],
  // Headings: the hashes go, the words stay on their own line.
  [/^\s{0,3}#{1,6}\s+/gm, ""],
  // A horizontal rule carries nothing a reader needs in plain text.
  [/^\s{0,3}([-*_])(?:\s*\1){2,}\s*$/gm, ""],
  // Bullets become a bullet character, so a list still reads as a list.
  [/^(\s*)[-*+]\s+/gm, "$1• "],
  // Block quotes lose their marker.
  [/^\s{0,3}>\s?/gm, ""],
  // [words](address) → words (address); a bare address keeps itself.
  [/\[([^\]\n]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g, "$1 ($2)"],
  // Emphasis, strongest first so ** is not read as two *.
  [/\*\*([^*\n]+)\*\*/g, "$1"],
  [/__([^_\n]+)__/g, "$1"],
  [/(^|[\s(])\*([^*\n]+)\*(?=[\s).,;:!?]|$)/g, "$1$2"],
  [/(^|[\s(])_([^_\n]+)_(?=[\s).,;:!?]|$)/g, "$1$2"],
  // Inline code: the ticks go, the code stays as it was written.
  [/`([^`\n]+)`/g, "$1"],
];

export function plainAnswer(text: string): string {
  if (!text) return text;
  return RULES.reduce((value, [pattern, replacement]) => value.replace(pattern, replacement), text)
    // A removed heading or fence leaves its blank line behind; at most one is kept.
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
